import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import Select from "react-select";
// Theme-adaptive styles for react-select
const getSelectStyles = (isDark: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    "&:hover": {
      borderColor: isDark ? "#2563eb" : "#2563eb",
    },
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderColor: state.isFocused
      ? isDark
        ? "#2563eb"
        : "#2563eb"
      : isDark
        ? "#374151"
        : "#d1d5db",
    boxShadow: state.isFocused
      ? isDark
        ? "0 0 0 1px #2563eb"
        : "0 0 0 1px #2563eb"
      : undefined,
    color: isDark ? "#fff" : "#111",
  }),
  input: (base: any) => ({
    ...base,
    color: isDark ? "#fff" : "#111",
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: isDark ? "#1f2937" : "#fff",
    color: isDark ? "#fff" : "#111",
    zIndex: 9999,
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? isDark
        ? "#2563eb"
        : "#2563eb"
      : state.isFocused
        ? isDark
          ? "#374151"
          : "#e5e7eb"
        : isDark
          ? "#1f2937"
          : "#fff",
    color: state.isSelected ? "#fff" : isDark ? "#fff" : "#111",
    cursor: "pointer",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: isDark ? "#9ca3af" : "#6b7280",
  }),
  singleValue: (base: any) => ({
    ...base,
    color: isDark ? "#fff" : "#111",
  }),
});
import toast from "react-hot-toast";

import { menuService, orderService } from "@/services/MenuService";

interface AddOrderForm {
  address?: string;
  customerName: string;
  customerPhone: string;
  deliveryNotes?: string;
  driverId?: string;
  items: OrderItem[];
  orderType: "delivery" | "dine-in" | "takeaway";
  paymentMethod: string;
  promoCode?: string;
  status: string;
  tableNumber?: string;
}

interface AddOrderModalProps {
  drivers?: Driver[];
  menuItems?: MenuItem[];
  onClose: () => void;
  onSubmit: (data: AddOrderForm) => void;
  open: boolean;
}

interface Driver {
  id: string;
  name: string;
}

interface MenuItem {
  id: string;
  name: string;
  price: number;
}

interface OrderItem {
  itemId: string;
  quantity: number;
}

const AddOrderModal: React.FC<AddOrderModalProps> = ({
  drivers = [],
  menuItems: menuItemsProp = [],
  onClose,
  onSubmit,
  open,
}) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<AddOrderForm>({
    address: "",
    customerName: "Regular",
    customerPhone: "0026650480814",
    deliveryNotes: "",
    driverId: "",
    items: [{ itemId: "", quantity: 1 }],
    orderType: "delivery",
    paymentMethod: "",
    promoCode: "",
    status: "pending",
    tableNumber: "",
  });
  const [menuItems, setMenuItems] = useState<MenuItem[]>(menuItemsProp);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [menuItemsError, setMenuItemsError] = useState(false);

  // Search state for filtering menu items in dropdown
  const [menuItemSearch, setMenuItemSearch] = useState("");
  // Fetch menu items from database if not provided
  useEffect(() => {
    if (!open) return;
    if (menuItemsProp && menuItemsProp.length > 0) {
      setMenuItems(menuItemsProp);
      setMenuItemsError(false);
      return;
    }
    if (menuItems.length > 0 || loadingMenuItems || menuItemsError) return;
    setLoadingMenuItems(true);
    menuService
      .getMenuItems()
      .then((res: any) => {
        setMenuItems(res.data);
        setMenuItemsError(false);
      })
      .catch((err: any) => {
        setMenuItemsError(true);
        toast.error("Failed to load menu items");
      })
      .finally(() => setLoadingMenuItems(false));
  }, [open, menuItemsProp]);

  if (!open) return null;

  // ► Handle item row updates
  const updateItem = (index: number, field: string, value: any) => {
    const updated = [...form.items];
    (updated as any)[index][field] = value;
    setForm({ ...form, items: updated });
  };

  const addItemRow = () => {
    setForm({ ...form, items: [...form.items, { itemId: "", quantity: 1 }] });
  };

  const removeItemRow = (i: number) => {
    setForm({ ...form, items: form.items.filter((_, x) => x !== i) });
  };

  // ► Calculate totals
  const getSubtotal = () =>
    form.items.reduce((sum, row) => {
      const item = menuItems.find((m) => m.id === row.itemId);
      return sum + (item ? item.price * row.quantity : 0);
    }, 0);

  const VAT_RATE = 0.15;
  const DELIVERY_FEE = form.orderType === "delivery" ? 25 : 0;

  const getVAT = () => getSubtotal() * VAT_RATE;
  const getTotal = () => getSubtotal() + getVAT() + DELIVERY_FEE;

  const handleSubmit = async () => {
    // Calculate required fields
    const selectedItems = form.items
      .filter((row) => row.itemId)
      .map((row) => {
        const menuItem = menuItems.find((m) => m.id === row.itemId);
        return {
          item_id: menuItem?.id,
          name: menuItem?.name,
          price: menuItem?.price ?? 0,
          quantity: row.quantity,
        };
      });
    const subtotal = selectedItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
    const vat = subtotal * 0.15;
    const delivery_fee = form.orderType === "delivery" ? 25 : 0;
    const total = subtotal + vat + delivery_fee;

    // Map frontend fields to backend
    // Map orderType to backend values
    const orderTypeMap: Record<string, string> = {
      delivery: "Delivery",
      "dine-in": "Dine-in",
      takeaway: "Takeaway",
    };
    const payload = {
      customer_name: form.customerName,
      delivery_address: form.address,
      delivery_fee,
      delivery_notes: form.deliveryNotes,
      driver_id: form.driverId,
      items: selectedItems,
      order_type: orderTypeMap[form.orderType],
      payment_method: form.paymentMethod,
      phone_number: form.customerPhone,
      promo_code: form.promoCode,
      status: form.status,
      subtotal,
      table_number: form.tableNumber,
      total,
      vat,
    };
    try {
      await orderService.addOrder(payload);
      toast.success("Order created successfully!");
      onSubmit(form);
      onClose();
    } catch (err: any) {
      let errorMsg = "Failed to create order";
      if (err?.response?.data) {
        if (typeof err.response.data === "string") {
          errorMsg = err.response.data;
        } else if (err.response.data.message) {
          errorMsg = err.response.data.message;
        } else if (err.response.data.errors) {
          // Laravel validation errors
          errorMsg = Object.entries(err.response.data.errors)
            .map(
              ([field, msgs]) =>
                `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`,
            )
            .join(" | ");
        } else {
          errorMsg = JSON.stringify(err.response.data);
        }
      }
      toast.error(errorMsg);
      console.error("Order creation error:", err);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full bg-transparent p-2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-200"
          onClick={onClose}
          type="button"
        >
          <X className="h-6 w-6" />
        </button>

        {/* TITLE */}
        <h2 className="mb-4 text-2xl font-bold dark:text-white">
          Create New Order
        </h2>

        {/* STEP INDICATOR */}
        <div className="mb-6 flex items-center justify-between">
          {[1, 2, 3].map((n) => (
            <div
              className={`mx-1 h-2 flex-1 rounded ${
                step >= n ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-700"
              }`}
              key={n}
            />
          ))}
        </div>

        {/* ===================== STEP 1 — CUSTOMER ===================== */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold dark:text-white">
              Customer Details
            </h3>
            <input
              className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
              onChange={(e) =>
                setForm({ ...form, customerName: e.target.value })
              }
              placeholder="Customer Name"
              required
              value={form.customerName}
            />
            <input
              className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
              onChange={(e) =>
                setForm({ ...form, customerPhone: e.target.value })
              }
              placeholder="Phone Number"
              required
              value={form.customerPhone}
            />
            {/* ORDER TYPE */}
            <div>
              <label className="text-sm font-medium dark:text-gray-300">
                Order Type
              </label>
              <select
                className="mt-1 w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) =>
                  setForm({
                    ...form,
                    orderType: e.target.value as any,
                  })
                }
                value={form.orderType}
              >
                <option value="dine-in">Dine-In</option>
                <option value="takeaway">Takeaway</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>
            TABLE NUMBER IF DINE-IN
            {/* {form.orderType === "dine-in" && (
              <input
                className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                onChange={(e) =>
                  setForm({ ...form, tableNumber: e.target.value })
                }
                placeholder="Table Number"
                value={form.tableNumber}
              />
            )} */}
            <div className="flex justify-end">
              <button
                className="rounded bg-blue-600 px-4 py-2 text-white"
                onClick={() => setStep(2)}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ===================== STEP 2 — ITEMS ===================== */}
        {step === 2 && (
          <div>
            <h3 className="mb-4 text-lg font-semibold dark:text-white">
              Order Items
            </h3>

            {form.items.map((row, index) => {
              // Detect dark mode: check for 'dark' class on html or body
              const isDark =
                typeof document !== "undefined" &&
                (document.documentElement.classList.contains("dark") ||
                  document.body.classList.contains("dark"));
              return (
                <div className="mb-3 flex gap-2" key={index}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Select
                      isClearable
                      isDisabled={loadingMenuItems}
                      onChange={(opt) =>
                        updateItem(index, "itemId", opt ? opt.value : "")
                      }
                      options={menuItems.map((i) => ({
                        label: `${i.name} — R${i.price}`,
                        value: i.id,
                      }))}
                      placeholder={
                        loadingMenuItems ? "Loading..." : "Choose Item"
                      }
                      styles={getSelectStyles(isDark)}
                      value={
                        menuItems.find((i) => i.id === row.itemId)
                          ? {
                              label: `${menuItems.find((i) => i.id === row.itemId)?.name} — R${menuItems.find((i) => i.id === row.itemId)?.price}`,
                              value: row.itemId,
                            }
                          : null
                      }
                    />
                  </div>
                  <input
                    className="w-24 rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                    min={1}
                    onChange={(e) =>
                      updateItem(index, "quantity", Number(e.target.value))
                    }
                    type="number"
                    value={row.quantity}
                  />
                  {form.items.length > 1 && (
                    <button
                      className="rounded bg-red-500 px-3 py-2 text-white"
                      onClick={() => removeItemRow(index)}
                      type="button"
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}

            <button
              className="mt-2 rounded bg-gray-700 px-3 py-1 text-sm text-white"
              onClick={addItemRow}
              type="button"
            >
              + Add Item
            </button>

            {/* TOTAL SUMMARY */}
            <div className="mt-6 rounded bg-gray-100 p-4 dark:bg-gray-800">
              <p className="text-gray-700 dark:text-gray-300">
                Subtotal: R{getSubtotal().toFixed(2)}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                VAT (15%): R{getVAT().toFixed(2)}
              </p>
              {form.orderType === "delivery" && (
                <p className="text-gray-700 dark:text-gray-300">
                  Delivery Fee: R{DELIVERY_FEE}
                </p>
              )}
              <p className="mt-2 text-xl font-bold dark:text-white">
                Total: R{getTotal().toFixed(2)}
              </p>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                className="rounded bg-gray-500 px-4 py-2 text-white"
                onClick={() => setStep(1)}
              >
                ← Back
              </button>

              <button
                className="rounded bg-blue-600 px-4 py-2 text-white"
                onClick={() => setStep(3)}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ===================== STEP 3 — DELIVERY & PAYMENT ===================== */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="mb-2 text-lg font-semibold dark:text-white">
              Delivery & Payment
            </h3>

            {/* DELIVERY INFO */}
            {form.orderType === "delivery" && (
              <>
                <input
                  className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                  placeholder="Delivery Address"
                  value={form.address}
                />

                <textarea
                  className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                  onChange={(e) =>
                    setForm({ ...form, deliveryNotes: e.target.value })
                  }
                  placeholder="Delivery Notes (optional)"
                  rows={2}
                  value={form.deliveryNotes}
                />

                {/* Assign Driver */}
                <select
                  className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
                  onChange={(e) =>
                    setForm({ ...form, driverId: e.target.value })
                  }
                  value={form.driverId}
                >
                  <option value="">Assign Driver</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            {/* PAYMENT METHOD */}
            <select
              className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
              onChange={(e) =>
                setForm({ ...form, paymentMethod: e.target.value })
              }
              value={form.paymentMethod}
            >
              <option value="">Select Payment Method</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="eft">Bank Transfer</option>
            </select>

            {/* PROMO CODE */}
            <input
              className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
              onChange={(e) => setForm({ ...form, promoCode: e.target.value })}
              placeholder="Promo Code"
              value={form.promoCode}
            />

            {/* STATUS */}
            <select
              className="w-full rounded border px-3 py-2 dark:bg-gray-800 dark:text-white"
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              value={form.status}
            >
              <option value="pending">Pending</option>
              <option value="preparing">Preparing</option>
              <option value="ready">Ready for Pickup</option>
              <option value="delivering">Out for Delivery</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* BUTTONS */}
            <div className="mt-6 flex justify-between">
              <button
                className="rounded bg-gray-500 px-4 py-2 text-white"
                onClick={() => setStep(2)}
              >
                ← Back
              </button>

              <button
                className="rounded bg-green-600 px-4 py-2 text-white"
                onClick={handleSubmit}
              >
                Submit Order ✓
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOrderModal;
